<template>
  <div class="variance-dashboard">
    <!-- Header -->
    <div class="vd-header">
      <div>
        <div class="vd-title">Variance Analysis</div>
        <div class="vd-subtitle">Performance metrics for Terminal vs Ship Figures</div>
      </div>
    </div>

    <!-- Stat cards -->
    <div class="stat-cards">
      <div class="stat-card">
        <div>
          <div class="stat-label">AVG. VARIANCE PER NOMINATION</div>
          <div class="stat-date-range">{{ chartWindowLabel.start }} – {{ chartWindowLabel.end }}</div>
          <div class="stat-value">{{ avgVariance }}</div>
        </div>
        
        <div class="stat-icon stat-icon--blue">
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="white">
            <!-- stem -->
            <rect x="11.3" y="4" width="1.4" height="14" rx="0.7"/>
            <!-- base -->
            <rect x="7" y="18" width="10" height="1.5" rx="0.75"/>
            <!-- crossbar -->
            <rect x="3" y="5.5" width="18" height="1.5" rx="0.75"/>
            <!-- left pan strings -->
            <line x1="4" y1="7" x2="1.5" y2="10.5" stroke="white" stroke-width="1" stroke-linecap="round"/>
            <line x1="4" y1="7" x2="6.5" y2="10.5" stroke="white" stroke-width="1" stroke-linecap="round"/>
            <!-- left pan: plate shape (outer arc down, inner arc back = saucer rim) -->
            <path d="M 1.5,10.5 Q 4,13.5 6.5,10.5 Q 4,11.8 1.5,10.5 Z"/>
            <!-- right pan strings -->
            <line x1="20" y1="7" x2="17.5" y2="10.5" stroke="white" stroke-width="1" stroke-linecap="round"/>
            <line x1="20" y1="7" x2="22.5" y2="10.5" stroke="white" stroke-width="1" stroke-linecap="round"/>
            <!-- right pan: plate shape -->
            <path d="M 17.5,10.5 Q 20,13.5 22.5,10.5 Q 20,11.8 17.5,10.5 Z"/>
          </svg>
        </div>
      </div>
      <div class="stat-card">
        <div>
          <div class="stat-label">{{ totalLossIsGain ? 'TOTAL QUANTITY GAIN' : 'TOTAL QUANTITY LOSS' }}</div>
          <div class="stat-date-range">{{ chartWindowLabel.start }} – {{ chartWindowLabel.end }}</div>
          <div class="stat-value">{{ totalLoss }}</div>
        </div>
        <div :class="['stat-icon', totalLossIsGain ? 'stat-icon--green' : 'stat-icon--red']">
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="white">
            <path v-if="totalLossIsGain"
              d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z" />
            <path v-else d="M16 18l2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6h-6z" />
          </svg>
        </div>
      </div>
    </div>

    <!-- Monthly chart -->
    <div class="chart-card">
      <div class="chart-card-header">
        <div class="chart-title-nav">
          <span class="chart-card-title">Monthly Net Variance (MT)</span>
          <div class="chart-nav">
            <button class="chart-nav-btn" @click="chartOffset += 1; fetchChart()">&#8249;</button>
            <span class="chart-window-label">{{ chartWindowLabel.start }} – {{ chartWindowLabel.end }}</span>
            <button class="chart-nav-btn" :disabled="chartOffset === 0" @click="chartOffset -= 1; fetchChart()">&#8250;</button>
          </div>
        </div>
        <div class="chart-legend">
          <span v-for="l in chartLegend" :key="l.name" class="legend-item">
            <span class="legend-dot" :style="{ background: l.color }"></span>
            {{ l.name }}
          </span>
        </div>
      </div>
      <vue-echarts :option="chartOption" style="height: 340px; width: 100%;" />
      <!-- <div class="barges-row">
        <div v-for="(m, i) in monthlyData" :key="i" class="barge-item">
          <div class="barge-label">BARGES</div>
          <div class="barge-value">{{ m.barges }}</div>
        </div>
      </div> -->
    </div>

    <!-- Ranking filter -->
    <div class="ranking-filter-bar">
      <span class="ranking-filter-label">Date Range</span>
      <el-date-picker
        v-model="rankingDateRange"
        type="daterange"
        size="small"
        value-format="yyyy-MM-dd"
        range-separator="–"
        start-placeholder="Start date"
        end-placeholder="End date"
        @change="onRankingDateChange"
      />
    </div>

    <!-- Ranking panels -->
    <div class="ranking-row">
      <div class="ranking-card" v-for="panel in rankingPanels" :key="panel.title" v-loading="panel.loading">
        <div class="ranking-card-header">
          <span class="ranking-title">{{ panel.title }}</span>
          <div class="ranking-tabs">
            <button :class="['ranking-tab', { active: panel.activeTab === 'loss' }]"
              @click="setRankingTab(panel, 'loss')">HIGHEST LOSS</button>
            <button :class="['ranking-tab gain', { active: panel.activeTab === 'gain' }]"
              @click="setRankingTab(panel, 'gain')">HIGHEST GAIN</button>
          </div>
        </div>
        <div class="ranking-list">
          <div v-for="(item, idx) in getSortedItems(panel)" :key="idx"
            :class="['ranking-item', { clickable: panel.key === 'company' || panel.key === 'barge' || panel.key === 'team' }]"
            @click="panel.key === 'team' ? openTeamDetails(item) : (panel.key === 'company' || panel.key === 'barge') && openCompanyDetails(item, panel.key)">
            <span class="rank-num">{{ idx + 1 }}</span>
            <div class="rank-info">
              <div class="rank-name-row">
                <span class="rank-name">{{ item.name }}</span>
                <span v-if="item.subtitle" class="rank-subtitle">{{ item.subtitle }}</span>
              </div>
              <div class="rank-bar-row">
                <div class="rank-bar" :style="{
                  width: getBarWidth(item.variance, panel) + '%',
                  background: item.variance < 0 ? '#ef4444' : '#22c55e'
                }"></div>
              </div>
            </div>
            <div class="rank-meta">
              <span class="rank-jobs">{{ item.jobs }} Jobs</span>
              <span :class="['rank-variance', item.variance < 0 ? 'loss' : 'gain']">
                {{ item.variance > 0 ? '+' : '' }}{{ item.variance.toFixed(3) }}
              </span>
              <span class="rank-avg-label">MT avg</span>
              <span v-if="(panel.key === 'company' || panel.key === 'barge') && item.avgPct !== null"
                :class="['rank-avg-pct', Number(item.avgPct) < 0 ? 'loss' : 'gain']">
                {{ Number(item.avgPct) >= 0 ? '+' : '' }}{{ item.avgPct }}%
              </span>
            </div>
          </div>
        </div>

        <div class="ranking-footer" v-if="panel.visibleCount < panel.items.length">
          <span class="view-full" @click="panel.visibleCount += 10">Load More</span>
        </div>
      </div>
    </div>

    <!-- Team details modal -->
    <el-dialog :visible.sync="teamModal.visible" :title="null" width="1200px" :close-on-click-modal="false"
      custom-class="company-modal">
      <template slot="title">
        <div class="cm-title">
          <span class="cm-name">{{ teamModal.item && teamModal.item.name }}</span>
        </div>
        <div class="cm-stats" v-if="teamModal.item">
          <span>Total Jobs: <b>{{ teamModal.total }}</b></span>
          <span>Net Variance:
            <b :class="teamModalNetVariance >= 0 ? 'cm-gain' : 'cm-loss'">
              {{ teamModalNetVariance >= 0 ? '+' : '' }}{{ teamModalNetVariance.toFixed(1) }} MT
            </b>
          </span>
          <!-- <span v-if="teamModal.avgPct !== null">Avg %:
            <b :class="Number(teamModal.avgPct) >= 0 ? 'cm-gain' : 'cm-loss'">
              {{ Number(teamModal.avgPct) >= 0 ? '+' : '' }}{{ teamModal.avgPct }}%
            </b>
          </span> -->
        </div>
      </template>

      <div v-loading="teamModal.loading">
        <div class="tm-section-title">Team Member Contributions</div>
        <div class="tm-members-grid">
          <div
            :class="['tm-member-card', { 'tm-member-card--active': teamModal.filterMember === m.userId }]"
            v-for="m in teamModal.members" :key="m.userId"
            @click="teamModal.filterMember = teamModal.filterMember === m.userId ? null : m.userId; onTeamMemberFilter()">
            <div class="tm-member-name">{{ m.memberName }}</div>
            <div class="tm-member-jobs">{{ m.totalJob }} Jobs</div>
            <div :class="['tm-member-variance', m.totalVarianceMetricTon >= 0 ? 'cm-gain' : 'cm-loss']">
              {{ m.totalVarianceMetricTon >= 0 ? '+' : '' }}{{ m.totalVarianceMetricTon }} MT
            </div>
            <!-- <div class="tm-member-pct">
              {{ m.percentage }}%
            </div> -->
          </div>
        </div>

        <div class="cm-table-header" style="margin-top: 20px;">
          <span class="cm-section">Recent Nominations</span>
          <div style="display:flex; align-items:center; gap: 12px; flex-wrap: wrap;">
            <span style="font-size: 13px; color: #64748b;">Filter by Member:</span>
            <el-select v-model="teamModal.filterMember" size="small" style="width: 160px;" @change="onTeamMemberFilter">
              <el-option label="All Members" :value="null" />
              <el-option v-for="m in teamModal.members" :key="m.userId" :label="m.memberName" :value="m.userId" />
            </el-select>
            <span class="cm-count">Showing {{ teamModal.jobs.length }} of {{ teamModal.total }} records</span>
          </div>
        </div>

        <el-table v-loading="teamModal.jobsLoading" :data="teamModal.jobs" size="small" max-height="300"
          style="width:100%">
          <el-table-column label="JOB ID" min-width="90">
            <template slot-scope="{ row }">NOM-{{ row.nomId }}</template>
          </el-table-column>
          <el-table-column label="DATE" prop="dateTime" min-width="155" />
          <el-table-column label="TEAM MEMBER" prop="memberName" min-width="140" />
          <el-table-column label="VESSEL" prop="bargeName" min-width="130" />
          <el-table-column label="PRODUCT" min-width="100">
            <template slot-scope="{ row }">
              <span :class="['product-badge', productBadgeClass(row.product)]">{{ row.product }}</span>
            </template>
          </el-table-column>
          <el-table-column label="TERMINAL FIGURE" prop="terminalFigure" min-width="130" align="right" />
          <el-table-column label="SHIP ULLAGE FIGURE" prop="shipUllageFigure" min-width="140" align="right" />
          <el-table-column label="DIFF (MT)" min-width="95" align="right">
            <template slot-scope="{ row }">
              <span :class="row.varianceMetricTon >= 0 ? 'cm-gain' : 'cm-loss'">
                {{ row.varianceMetricTon >= 0 ? '+' : '' }}{{ row.varianceMetricTon }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="% VAR" min-width="85" align="right">
            <template slot-scope="{ row }">
              <span :class="row.variancePercentage >= 0 ? 'cm-gain' : 'cm-loss'">
                {{ row.variancePercentage }}%
              </span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <span slot="footer" class="cm-footer">
        <el-pagination :current-page="teamModal.pageNum" :page-size="teamModal.pageSize" :page-sizes="[10, 20, 50]"
          :total="teamModal.total" layout="total, sizes, prev, pager, next" @current-change="onTeamPageChange"
          @size-change="onTeamSizeChange" />
        <el-button @click="teamModal.visible = false">Close Details</el-button>
      </span>
    </el-dialog>

    <!-- Company details modal -->
    <el-dialog :visible.sync="companyModal.visible" :title="null" width="1100px" :close-on-click-modal="false"
      custom-class="company-modal">
      <template slot="title">
        <div class="cm-title">
          <span class="cm-name">{{ companyModal.item && companyModal.item.name }}</span>
        </div>
        <div class="cm-stats" v-if="companyModal.item">
          <span>Total Jobs: <b>{{ companyModal.total }}</b></span>
          <span>Net Variance:
            <b :class="(companyModal.item.totalVariance ?? companyModal.item.variance) >= 0 ? 'cm-gain' : 'cm-loss'">
              {{ (companyModal.item.totalVariance ?? companyModal.item.variance) >= 0 ? '+' : '' }}{{
                (companyModal.item.totalVariance ?? companyModal.item.variance).toFixed(1) }} MT
            </b>
          </span>
          <!-- <span v-if="companyModal.avgPct !== null">Avg %:
            <b :class="Number(companyModal.avgPct) >= 0 ? 'cm-gain' : 'cm-loss'">
              {{ Number(companyModal.avgPct) >= 0 ? '+' : '' }}{{ companyModal.avgPct }}%
            </b>
          </span> -->
        </div>
      </template>

      <div class="cm-table-header">
        <span class="cm-section">Recent Nominations</span>
        <span class="cm-count">Showing {{ companyModal.jobs.length }} of {{ companyModal.total }} records</span>
      </div>

      <el-table v-loading="companyModal.loading" :data="companyModal.jobs" size="small" max-height="460"
        style="width:100%">
        <el-table-column label="JOB ID" min-width="90">
          <template slot-scope="{ row }">NOM-{{ row.nomId }}</template>
        </el-table-column>
        <el-table-column label="DATE" prop="dateTime" min-width="155" />
        <el-table-column label="VESSEL" prop="bargeName" min-width="130" />
        <el-table-column label="PRODUCT" min-width="100">
          <template slot-scope="{ row }">
            <span :class="['product-badge', productBadgeClass(row.product)]">{{ row.product }}</span>
          </template>
        </el-table-column>
        <el-table-column label="TERMINAL FIGURE" prop="terminalFigure" min-width="120" align="right" />
        <el-table-column label="SHIP ULLAGE FIGURE" prop="shipUllageFigure" min-width="130" align="right" />
        <el-table-column label="DIFF (MT)" min-width="95" align="right">
          <template slot-scope="{ row }">
            <span :class="row.varianceMetricTon >= 0 ? 'cm-gain' : 'cm-loss'">
              {{ row.varianceMetricTon >= 0 ? '+' : '' }}{{ row.varianceMetricTon }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="% VAR" min-width="85" align="right">
          <template slot-scope="{ row }">
            <span :class="row.variancePercentage >= 0 ? 'cm-gain' : 'cm-loss'">
              {{ row.variancePercentage }}%
            </span>
          </template>
        </el-table-column>
      </el-table>

      <span slot="footer" class="cm-footer">
        <el-pagination :current-page="companyModal.pageNum" :page-size="companyModal.pageSize"
          :page-sizes="[10, 20, 50]" :total="companyModal.total" layout="total, sizes, prev, pager, next"
          @current-change="onModalPageChange" @size-change="onModalSizeChange" />
        <el-button @click="companyModal.visible = false">Close Details</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import 'primeicons/primeicons.css';
import { VueEcharts } from 'vue3-echarts';
import moment from 'moment';
import {
  getCompanyVarianceSummary,
  getTeamVarianceSummary,
  getBargeVarianceSummary,
  getMonthlyNetVariance,
  getCompanyRecentJob,
  getBargeRecentJob,
  getTeamDetails,
  getTeamRecentJob,
} from '@/services/index';

export default {
  name: 'VarianceDashboard',
  components: { VueEcharts },
  data() {
    return {
      avgVariance: '--',
      totalLoss: '--',
      totalLossIsGain: false,
      companyModal: {
        visible: false,
        loading: false,
        type: 'company',
        item: null,
        jobs: [],
        total: 0,
        avgPct: null,
        pageNum: 1,
        pageSize: 20,
      },
      chartOffset: 0,
      chartLoading: false,
      monthlyData: [],
      uniqueCompanies: [],
      rankingDateRange: [
        moment().startOf('month').format('YYYY-MM-DD'),
        moment().endOf('month').format('YYYY-MM-DD'),
      ],
      rankingPanels: [
        { title: 'Customer User Variance Ranking', key: 'company', activeTab: 'loss', items: [], loading: false, visibleCount: 10 },
        { title: 'Team Variance Ranking', key: 'team', activeTab: 'loss', items: [], loading: false, visibleCount: 10 },
        { title: 'Barge Variance Ranking', key: 'barge', activeTab: 'loss', items: [], loading: false, visibleCount: 10 },
      ],
      teamModal: {
        visible: false,
        loading: false,
        jobsLoading: false,
        item: null,
        members: [],
        jobs: [],
        total: 0,
        avgPct: null,
        pageNum: 1,
        pageSize: 20,
        filterMember: null,
      },
    };
  },
  async created() {
    await this.fetchAll();
  },
  computed: {
    teamModalNetVariance() {
      if (this.teamModal.filterMember != null && this.teamModal.members.length) {
        const member = this.teamModal.members.find(m => m.userId === this.teamModal.filterMember);
        if (member) return member.totalVarianceMetricTon ?? 0;
      }
      return this.teamModal.item?.totalVariance ?? this.teamModal.item?.variance ?? 0;
    },

    dateRange() {
      const fmt = 'YYYY-MM-DD HH:mm:ss';
      return {
        startDate: moment().subtract(5, 'months').startOf('month').format(fmt),
        endDate: moment().endOf('month').format(fmt),
      };
    },
    chartDateRange() {
      const fmt = 'YYYY-MM-DD HH:mm:ss';
      return {
        startDate: moment().subtract(5 + this.chartOffset, 'months').startOf('month').format(fmt),
        endDate: moment().subtract(this.chartOffset, 'months').endOf('month').format(fmt),
      };
    },
    chartWindowLabel() {
      return {
        start: moment().subtract(5 + this.chartOffset, 'months').startOf('month').format('MMM YYYY'),
        end: moment().subtract(this.chartOffset, 'months').endOf('month').format('MMM YYYY'),
      };
    },
    chartLegend() {
      return [
        ...this.uniqueCompanies.map(c => ({ name: c.name, color: c.color })),
        { name: 'Trend Value', color: '#74c0fc' },
      ];
    },
    chartOption() {
      const months = this.monthlyData.map(m => m.monthLabel);
      const monthlyData = this.monthlyData;

      // 6-month rolling average — prefer variance % (secondary axis) when BDR data exists,
      // else fall back to raw MT totals (primary axis) so the line is always visible
      const movingAvgPct = monthlyData.map((_, i) => {
        const win = monthlyData.slice(Math.max(0, i - 5), i + 1);
        const valid = win.filter(m => m.variancePct != null);
        if (valid.length === 0) return null;
        return parseFloat((valid.reduce((s, m) => s + Number(m.variancePct), 0) / valid.length).toFixed(2));
      });
      const usePctTrend = movingAvgPct.some(v => v != null);
      const trendData = usePctTrend
        ? movingAvgPct
        : monthlyData.map((_, i) => {
          const win = monthlyData.slice(Math.max(0, i - 5), i + 1);
          return parseFloat((win.reduce((s, m) => s + m.total, 0) / win.length).toFixed(1));
        });
      const trendAxisIdx = usePctTrend ? 1 : 0;

      const barSeries = this.uniqueCompanies.map(company => ({
        name: company.name,
        type: 'bar',
        stack: 'total',
        yAxisIndex: 0,
        data: monthlyData.map(m => {
          const c = m.companies.find(c => c.name === company.name);
          return c ? parseFloat(c.variance.toFixed(2)) : 0;
        }),
        itemStyle: { color: company.color },
      }));

      return {
        grid: { left: 40, right: 55, top: 20, bottom: 10, containLabel: true },
        xAxis: {
          type: 'category',
          data: months,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#94a3b8', fontSize: 12 },
          splitLine: { show: false },
        },
        yAxis: [
          {
            type: 'value',
            splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } },
            axisLabel: { color: '#94a3b8', fontSize: 11 },
            axisLine: { show: false },
            axisTick: { show: false },
          },
          {
            type: 'value',
            position: 'right',
            show: usePctTrend,
            axisLabel: { color: '#74c0fc', fontSize: 11, formatter: v => v + '%' },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
          },
        ],
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter(params) {
            const month = params[0]?.axisValue;
            const md = monthlyData.find(m => m.monthLabel === month);
            if (!md) return month;
            const mIdx = months.indexOf(month);
            const ma = trendData[mIdx];

            const pctStr = md.variancePct != null
              ? ` <span style="color:${Number(md.variancePct) >= 0 ? '#16a34a' : '#ef4444'};font-weight:700;">${Number(md.variancePct) >= 0 ? '+' : ''}${md.variancePct}%</span>`
              : '';
            const maStr = ma != null
              ? `&nbsp;&nbsp;<span style="color:#74c0fc;font-size:11px;">Trend Value: <b>${ma >= 0 ? '+' : ''}${ma}${usePctTrend ? '%' : ' MT'}</b></span>`
              : '';

            let html = `<div style="min-width:240px;">`
              + `<b>${month}</b>${pctStr}${maStr}<br/>`
              + `<span style="color:#94a3b8;font-size:11px;">Nominations handled: <b style="color:#1e293b;">${md.barges}</b></span>`
              + `</div><div style="border-top:1px solid #e2e8f0;margin:6px 0;"></div>`;

            params.forEach(p => {
              if (p.seriesName === 'Trend Value' || p.value === 0) return;
              const co = md.companies.find(c => c.name === p.seriesName);
              const pct = co?.bdrQty > 0
                ? `<span style="color:${co.variance >= 0 ? '#16a34a' : '#ef4444'};margin-left:6px;">${co.variance >= 0 ? '+' : ''}${(co.variance / co.bdrQty * 100).toFixed(2)}%</span>`
                : '';
              const jobsStr = co
                ? `<span style="color:#94a3b8;font-size:11px;margin-left:6px;">· ${co.jobs} noms</span>`
                : '';
              html += `<div style="display:flex;align-items:center;gap:4px;margin:3px 0;">`
                + `<span style="display:inline-block;width:9px;height:9px;border-radius:2px;background:${p.color};flex-shrink:0;"></span>`
                + `<span style="flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:130px;">${p.seriesName}</span>`
                + `<b style="color:${p.value >= 0 ? '#16a34a' : '#ef4444'};white-space:nowrap;">${p.value >= 0 ? '+' : ''}${Number(p.value).toFixed(1)} MT</b>`
                + pct + jobsStr
                + `</div>`;
            });

            const totalColor = md.total >= 0 ? '#16a34a' : '#ef4444';
            html += `<div style="border-top:1px solid #e2e8f0;margin:6px 0;"></div>`
              + `<div style="display:flex;justify-content:space-between;align-items:center;">`
              + `<b>Total</b>`
              + `<span>`
              + `<b style="color:${totalColor};">${md.total >= 0 ? '+' : ''}${md.total.toFixed(1)} MT</b>`
              + (md.variancePct != null ? `<b style="color:${Number(md.variancePct) >= 0 ? '#16a34a' : '#ef4444'};margin-left:8px;">${Number(md.variancePct) >= 0 ? '+' : ''}${md.variancePct}%</b>` : '')
              + `</span></div>`;
            return html;
          },
        },
        series: [
          ...barSeries,
          {
            name: 'Trend Value',
            type: 'line',
            yAxisIndex: trendAxisIdx,
            data: trendData,
            smooth: true,
            symbol: 'circle',
            symbolSize: 7,
            lineStyle: { color: '#74c0fc', width: 2.5 },
            itemStyle: { color: '#74c0fc' },
            label: { show: false },
          },
        ],
      };
    },
  },
  methods: {
    async fetchAll() {
      await Promise.all([
        this.fetchChart(),
        ...this.rankingPanels.map(p => this.fetchPanel(p)),
      ]);
    },

    async fetchChart() {
      const COLORS = [
        '#5470c6', '#2ca25f', '#f4c430', '#7b2d8b', '#e03030',
        '#29b6cf', '#f97316', '#84cc16', '#e91e8c', '#3d8f3d',
        '#ff6b6b', '#4ecdc4', '#45b7d1',
      ];
      const res = await getMonthlyNetVariance(this.chartDateRange);
      const apiData = res?.data ?? [];

      // Always build exactly 6 months for the current window
      const windowMonths = [];
      for (let i = 5; i >= 0; i--) {
        windowMonths.push(moment().subtract(i + this.chartOffset, 'months').format('YYYY-MM'));
      }

      // Collect unique companies in first-seen order
      const seen = new Map();
      apiData.forEach(item => {
        item.companyList.forEach(c => {
          if (!seen.has(c.companyName)) seen.set(c.companyName, c.companyId);
        });
      });
      this.uniqueCompanies = Array.from(seen.entries()).map(([name], i) => ({
        name,
        color: COLORS[i % COLORS.length],
      }));

      // Map API data keyed by month string
      const apiByMonth = new Map(apiData.map(item => [item.month, item]));

      this.monthlyData = windowMonths.map(month => {
        const item = apiByMonth.get(month);
        if (!item) {
          return {
            monthLabel: moment(month, 'YYYY-MM').format('MMM YYYY'),
            barges: 0,
            companies: [],
            total: 0,
            bdrQty: 0,
            variancePct: null,
          };
        }
        const companies = item.companyList.map(c => ({
          name: c.companyName,
          variance: c.totalVarianceMetricTon ?? 0,
          bdrQty: c.totalTerminalFigure ?? 0,
          jobs: c.totalJob ?? 0,
        }));
        const total = companies.reduce((s, c) => s + c.variance, 0);
        const bdrQty = companies.reduce((s, c) => s + c.bdrQty, 0);
        const barges = companies.reduce((s, c) => s + c.jobs, 0);
        return {
          monthLabel: moment(month, 'YYYY-MM').format('MMM YYYY'),
          barges,
          companies,
          total,
          bdrQty,
          variancePct: bdrQty > 0 ? (total / bdrQty * 100).toFixed(2) : null,
        };
      });

      let totalVariance = 0;
      let totalNominations = 0;
      apiData.forEach(item => {
        item.companyList.forEach(c => {
          totalVariance += c.totalVarianceMetricTon ?? 0;
          totalNominations += c.totalJob ?? 0;
        });
      });

      this.avgVariance = totalNominations > 0
        ? (totalVariance / totalNominations).toFixed(3) + ' MT'
        : '--';
      this.totalLoss = totalVariance.toFixed(1) + ' MT';
      this.totalLossIsGain = totalVariance >= 0;
    },

    async fetchPanel(panel) {
      panel.loading = true;
      const params = {
        sortType: panel.activeTab === 'loss' ? 'highestLoss' : 'highestGain',
        startDate: this.rankingDateRange ? this.rankingDateRange[0] + ' 00:00:00' : undefined,
        endDate: this.rankingDateRange ? this.rankingDateRange[1] + ' 23:59:59' : undefined,
      };
      try {
        let res;
        if (panel.key === 'company') res = await getCompanyVarianceSummary(params);
        else if (panel.key === 'team') res = await getTeamVarianceSummary(params);
        else res = await getBargeVarianceSummary(params);

        if (res?.data) {
          panel.items = res.data.map(item => this.mapItem(panel.key, item));
          panel.visibleCount = 10;
        }
      } finally {
        panel.loading = false;
      }
    },

    mapItem(key, item) {
      if (key === 'team') {
        const totalVariance = item.totalVarianceMetricTon ?? 0;
        const jobs = item.totalJob ?? 0;
        return {
          name: item.teamName,
          teamId: item.teamId,
          jobs,
          variance: jobs > 0 ? totalVariance / jobs : 0,
          totalVariance,
        };
      }
      if (key === 'barge') {
        const totalVariance = item.totalVarianceMetricTon ?? 0;
        const jobs = item.totalJob ?? 0;
        const totalBdrQty = item.totalTerminalFigure ?? 0;
        const totalVarPct = item.totalVariancePercentage ?? null;
        return {
          name: item.bargeName,
          bargeImo: item.bargeImo,
          jobs,
          variance: jobs > 0 ? totalVariance / jobs : 0,
          totalVariance,
          totalBdrQty,
          avgPct: jobs > 0 && totalVarPct !== null ? (totalVarPct / jobs).toFixed(2) : null,
        };
      }
      // company / user (loading master)
      const totalVariance = item.totalVarianceMetricTon ?? 0;
      const jobs = item.totalJob ?? 0;
      const totalBdrQty = item.totalTerminalFigure ?? 0;
      const totalVarPct = item.totalVariancePercentage ?? null;
      return {
        name: item.companyName,
        companyId: item.companyId,
        jobs,
        variance: jobs > 0 ? totalVariance / jobs : 0,
        totalVariance,
        totalBdrQty,
        avgPct: jobs > 0 && totalVarPct !== null ? (totalVarPct / jobs).toFixed(2) : null,
      };
    },

    onRankingDateChange(val) {
      if (!val) {
        this.rankingDateRange = [
          moment().startOf('month').format('YYYY-MM-DD'),
          moment().endOf('month').format('YYYY-MM-DD'),
        ];
      }
      this.rankingPanels.forEach(p => this.fetchPanel(p));
      if (this.teamModal.visible && this.teamModal.item) this._refreshTeamModal();
      if (this.companyModal.visible && this.companyModal.item) {
        this.companyModal.pageNum = 1;
        this.fetchCompanyJobs();
      }
    },

    async _refreshTeamModal() {
      this.teamModal.loading = true;
      this.teamModal.jobs = [];
      this.teamModal.total = 0;
      this.teamModal.avgPct = null;
      this.teamModal.pageNum = 1;
      this.teamModal.filterMember = null;
      await this._fetchTeamDetails();
      await Promise.all([this._fetchTeamJobs(), this._computeTeamAvgPct()]);
      this.teamModal.loading = false;
    },

    setRankingTab(panel, tab) {
      panel.activeTab = tab;
      this.fetchPanel(panel);
    },

    async openTeamDetails(item) {
      this.teamModal.item = item;
      this.teamModal.members = [];
      this.teamModal.jobs = [];
      this.teamModal.total = 0;
      this.teamModal.avgPct = null;
      this.teamModal.pageNum = 1;
      this.teamModal.filterMember = null;
      this.teamModal.loading = true;
      this.teamModal.visible = true;
      await this._fetchTeamDetails();
      await Promise.all([this._fetchTeamJobs(), this._computeTeamAvgPct()]);
      this.teamModal.loading = false;
    },

    async _computeTeamAvgPct() {
      const members = this.teamModal.members;
      if (!members.length) { this.teamModal.avgPct = null; return; }
      const { pageSize } = this.teamModal;
      const dateParams = {
        startDate: this.rankingDateRange ? this.rankingDateRange[0] + ' 00:00:00' : undefined,
        endDate: this.rankingDateRange ? this.rankingDateRange[1] + ' 23:59:59' : undefined,
      };
      const allJobs = [];
      let pageNum = 1;
      while (true) {
        const res = await getTeamRecentJob(this.teamModal.item.teamId, { pageNum, pageSize, ...dateParams });
        const rows = res?.rows ?? [];
        allJobs.push(...rows);
        if (allJobs.length >= (res?.total ?? 0) || rows.length === 0) break;
        pageNum++;
      }
      const byMember = {};
      allJobs.forEach(j => {
        if (!byMember[j.signById]) byMember[j.signById] = { sum: 0, count: 0 };
        byMember[j.signById].sum += j.variancePercentage ?? 0;
        byMember[j.signById].count += 1;
      });
      const memberAvgs = members.map(m => {
        const d = byMember[m.userId];
        return d ? d.sum / d.count : 0;
      });
      this.teamModal.avgPct = (memberAvgs.reduce((s, a) => s + a, 0) / members.length).toFixed(2);
    },

    async _fetchTeamDetails() {
      const startDate = this.rankingDateRange ? this.rankingDateRange[0] + ' 00:00:00' : undefined;
      const endDate = this.rankingDateRange ? this.rankingDateRange[1] + ' 23:59:59' : undefined;
      const res = await getTeamDetails(this.teamModal.item.teamId, { startDate, endDate });
      if (res?.data?.memberList) {
        this.teamModal.members = res.data.memberList;
      }
    },

    async _fetchTeamJobs() {
      const { item, pageNum, pageSize, filterMember } = this.teamModal;
      const params = {
        pageNum,
        pageSize,
        startDate: this.rankingDateRange ? this.rankingDateRange[0] + ' 00:00:00' : undefined,
        endDate: this.rankingDateRange ? this.rankingDateRange[1] + ' 23:59:59' : undefined,
      };
      if (filterMember != null) params.signById = filterMember;
      const res = await getTeamRecentJob(item.teamId, params);
      if (res) {
        this.teamModal.jobs = res.rows ?? [];
        this.teamModal.total = res.total ?? 0;
      }
    },

    async onTeamMemberFilter() {
      this.teamModal.pageNum = 1;
      this.teamModal.jobsLoading = true;
      await this._fetchTeamJobs();
      this.teamModal.jobsLoading = false;
    },

    onTeamPageChange(page) {
      this.teamModal.pageNum = page;
      this.teamModal.jobsLoading = true;
      this._fetchTeamJobs().finally(() => { this.teamModal.jobsLoading = false; });
    },

    onTeamSizeChange(size) {
      this.teamModal.pageSize = size;
      this.teamModal.pageNum = 1;
      this.teamModal.jobsLoading = true;
      this._fetchTeamJobs().finally(() => { this.teamModal.jobsLoading = false; });
    },

    async openCompanyDetails(item, type = 'company') {
      this.companyModal.type = type;
      this.companyModal.item = item;
      this.companyModal.pageNum = 1;
      this.companyModal.jobs = [];
      this.companyModal.total = 0;
      this.companyModal.avgPct = null;
      this.companyModal.visible = true;
      await this.fetchCompanyJobs();
    },

    async fetchCompanyJobs() {
      this.companyModal.loading = true;
      const { type, item, pageNum, pageSize } = this.companyModal;
      const dateParams = {
        pageNum, pageSize,
        startDate: this.rankingDateRange ? this.rankingDateRange[0] + ' 00:00:00' : undefined,
        endDate: this.rankingDateRange ? this.rankingDateRange[1] + ' 23:59:59' : undefined,
      };
      const res = type === 'barge'
        ? await getBargeRecentJob(item.bargeImo, dateParams)
        : await getCompanyRecentJob(item.companyId, dateParams);
      if (res) {
        const jobRows = res.rows ?? [];
        this.companyModal.jobs = jobRows;
        this.companyModal.total = res.total ?? 0;
        // avgPct = sum(variancePercentage) / count — use summary totals when available
        const summaryItem = this.companyModal.item;
        if (summaryItem.avgPct !== null) {
          this.companyModal.avgPct = summaryItem.avgPct;
        } else {
          const totalPct = jobRows.reduce((s, r) => s + (r.variancePercentage ?? 0), 0);
          this.companyModal.avgPct = jobRows.length > 0 ? (totalPct / jobRows.length).toFixed(2) : null;
        }
      }
      this.companyModal.loading = false;
    },

    onModalPageChange(page) {
      this.companyModal.pageNum = page;
      this.fetchCompanyJobs();
    },

    onModalSizeChange(size) {
      this.companyModal.pageSize = size;
      this.companyModal.pageNum = 1;
      this.fetchCompanyJobs();
    },

    productBadgeClass(product) {
      const p = (product ?? '').toUpperCase();
      if (p.includes('HSFO')) return 'badge-hsfo';
      if (p.includes('LSMGO')) return 'badge-lsmgo';
      return 'badge-lsfo';
    },

    getSortedItems(panel) {
      return panel.items.slice(0, panel.visibleCount);
    },

    getBarWidth(variance, panel) {
      const max = Math.max(...panel.items.map(i => Math.abs(i.variance)));
      return max > 0 ? (Math.abs(variance) / max) * 100 : 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.variance-dashboard {
  padding: 16px;
  background: #f1f5f9bb;
  min-height: 100%;
  border-radius: 10px;

  .vd-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;

    .vd-title {
      font-size: 22px;
      font-weight: 700;
      color: #1e293b;
    }

    .vd-subtitle {
      font-size: 13px;
      color: #94a3b8;
      margin-top: 2px;
    }

  }

  .stat-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 20px;

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }

    .stat-card {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 20px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .stat-label {
        font-size: 11px;
        font-weight: 600;
        color: #94a3b8;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        margin-bottom: 6px;
      }

      .stat-date-range {
        font-size: 11px;
        color: #94a3b8;
        margin-bottom: 4px;
      }

      .stat-value {
        font-size: 32px;
        font-weight: 700;
        color: #1e293b;
        line-height: 1.1;
      }

      .stat-icon {
        width: 52px;
        height: 52px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &--blue {
          background: #2563eb;
        }

        &--red {
          background: #ef4444;
        }

        &--green {
          background: #16a34a;
        }
      }
    }
  }

  .chart-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;

    .chart-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      flex-wrap: wrap;
      gap: 8px;

      .chart-title-nav {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .chart-nav {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .chart-nav-btn {
        background: white;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        width: 26px;
        height: 26px;
        font-size: 20px;
        line-height: 1;
        color: #64748b;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        transition: all 0.15s;

        &:hover:not(:disabled) {
          background: #f1f5f9;
          color: #2563eb;
          border-color: #cbd5e1;
        }

        &:disabled {
          opacity: 0.35;
          cursor: default;
        }
      }

      .chart-window-label {
        font-size: 12px;
        color: #64748b;
        font-weight: 500;
        white-space: nowrap;
      }

      .chart-card-title {
        font-size: 16px;
        font-weight: 600;
        color: #1e293b;
      }

      .chart-legend {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #64748b;

          .legend-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            flex-shrink: 0;
          }
        }
      }
    }

    .barges-row {
      display: flex;
      justify-content: space-around;
      margin-top: 12px;
      border-top: 1px solid #f1f5f9;
      padding-top: 12px;

      .barge-item {
        text-align: center;

        .barge-label {
          font-size: 9px;
          font-weight: 600;
          color: #94a3b8;
          letter-spacing: 0.05em;
        }

        .barge-value {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 4px 10px;
          margin-top: 4px;
        }
      }
    }
  }

  .ranking-filter-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;

    .ranking-filter-label {
      font-size: 13px;
      font-weight: 600;
      color: #64748b;
      white-space: nowrap;
    }
  }

  .ranking-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }

    .ranking-card {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 20px;
      display: flex;
      flex-direction: column;

      .ranking-card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 16px;
        gap: 8px;
        flex-wrap: wrap;

        .ranking-title {
          font-size: 14px;
          font-weight: 700;
          color: #1e293b;
          line-height: 1.3;
          max-width: 140px;
        }

        .ranking-tabs {
          display: flex;
          gap: 4px;

          .ranking-tab {
            padding: 4px 8px;
            font-size: 10px;
            font-weight: 600;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
            background: white;
            color: #94a3b8;
            cursor: pointer;
            transition: all 0.15s;
            letter-spacing: 0.02em;

            &.active {
              background: #fee2e2;
              border-color: #fca5a5;
              color: #ef4444;
            }

            &.gain.active {
              background: #dcfce7;
              border-color: #86efac;
              color: #16a34a;
            }

            &:hover:not(.active) {
              background: #f8fafc;
            }
          }
        }
      }

      .ranking-list {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .ranking-item {
          display: flex;
          align-items: center;
          gap: 8px;

          .rank-num {
            font-size: 12px;
            color: #94a3b8;
            width: 16px;
            text-align: center;
            flex-shrink: 0;
          }

          .rank-info {
            flex: 1;
            min-width: 0;

            .rank-name-row {
              display: flex;
              align-items: baseline;
              gap: 6px;
              margin-bottom: 4px;

              .rank-name {
                font-size: 13px;
                font-weight: 600;
                color: #1e293b;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }

              .rank-subtitle {
                font-size: 10px;
                color: #94a3b8;
                white-space: nowrap;
                text-transform: uppercase;
              }
            }

            .rank-bar-row {
              height: 4px;
              background: #f1f5f9;
              border-radius: 2px;
              overflow: hidden;

              .rank-bar {
                height: 100%;
                border-radius: 2px;
                transition: width 0.3s;
              }
            }
          }

          .rank-meta {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            flex-shrink: 0;
            gap: 2px;

            .rank-jobs {
              font-size: 10px;
              color: #94a3b8;
            }

            .rank-variance {
              font-size: 13px;
              font-weight: 700;

              &.loss {
                color: #ef4444;
              }

              &.gain {
                color: #16a34a;
              }
            }

            .rank-avg-label {
              font-size: 9px;
              color: #94a3b8;
              letter-spacing: 0.03em;
            }

            .rank-avg-pct {
              font-size: 10px;
              font-weight: 600;

              &.loss {
                color: #ef4444;
              }

              &.gain {
                color: #16a34a;
              }
            }
          }
        }
      }

      .ranking-footer {
        margin-top: 16px;
        border-top: 1px solid #f1f5f9;
        padding-top: 12px;
        text-align: center;

        .view-full {
          font-size: 13px;
          color: #64748b;
          cursor: pointer;
          transition: color 0.15s;

          &:hover {
            color: #2563eb;
          }
        }
      }
    }
  }

  .ranking-item.clickable {
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.15s;
    padding: 4px;
    margin: -4px;

    &:hover {
      background: #f8fafc;
    }
  }
}
</style>

<style lang="scss">
.company-modal {
  .el-dialog__header {
    padding-bottom: 0;
  }

  .el-dialog__body {
    padding-top: 12px;
  }

  .cm-title {
    display: flex;
    align-items: center;
    gap: 10px;

    .cm-name {
      font-size: 18px;
      font-weight: 700;
      color: #1e293b;
    }

    .cm-badge {
      font-size: 11px;
      font-weight: 600;
      padding: 3px 8px;
      border-radius: 6px;
      background: #f1f5f9;
      color: #64748b;
      border: 1px solid #e2e8f0;
    }
  }

  .cm-stats {
    display: flex;
    gap: 24px;
    margin-top: 6px;
    font-size: 13px;
    color: #64748b;

    b {
      color: #1e293b;
    }
  }

  .cm-table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .cm-section {
      font-size: 15px;
      font-weight: 600;
      color: #1e293b;
    }

    .cm-count {
      font-size: 12px;
      color: #94a3b8;
    }
  }

  .cm-gain {
    color: #16a34a;
    font-weight: 600;
  }

  .cm-loss {
    color: #ef4444;
    font-weight: 600;
  }

  .product-badge {
    font-size: 11px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 4px;

    &.badge-lsfo {
      background: #dbeafe;
      color: #1d4ed8;
    }

    &.badge-lsmgo {
      background: #dcfce7;
      color: #15803d;
    }

    &.badge-hsfo {
      background: #fef9c3;
      color: #a16207;
    }
  }

  .el-dialog__footer {
    border-top: 1px solid #f1f5f9;
    padding-top: 12px;
  }

  .cm-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .tm-section-title {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 12px;
  }

  .tm-members-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 10px;
    margin-bottom: 4px;
  }

  .tm-member-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px 14px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;

    &:hover {
      border-color: #3b82f6;
      background: #eff6ff;
    }

    &.tm-member-card--active {
      border-color: #3b82f6;
      background: #dbeafe;
    }

    .tm-member-name {
      font-size: 13px;
      font-weight: 600;
      color: #1e293b;
    }

    .tm-member-jobs {
      font-size: 11px;
      color: #94a3b8;
      margin-top: 2px;
    }

    .tm-member-variance {
      font-size: 16px;
      font-weight: 700;
      margin-top: 6px;
    }

    .tm-member-pct {
      font-size: 12px;
      margin-top: 1px;
    }
  }
}
</style>
